export interface AIAgentProject {
  name: string;
  slug: string;
  category: string;
  tier: 1 | 2 | 3;
  description: string;
  problem: string;
  capabilities: string[];
  technologies: string[];
  frameworks: string[];
  image: string;
  relatedServiceSlug?: string;
  industrySlug?: string;
}

export const agentCategories = [
  "All",
  "Healthcare",
  "Finance",
  "E-commerce",
  "Logistics & Fleet",
  "Real Estate",
  "Enterprise Ops",
  "Legal",
  "HR",
  "Sales & Marketing",
  "Travel",
];

export const aiAgents: AIAgentProject[] = [
  {
    name: "Medical Report Insights Agent",
    slug: "medical-report-insights-agent",
    category: "Healthcare",
    tier: 1,
    description: "Reads uploaded lab reports and medical PDFs, then surfaces plain-language health insights, flagged out-of-range values, and trend tracking across visits.",
    problem: "Patients and clinicians receive dense lab PDFs with no context on what changed or what needs attention, leading to missed follow-ups.",
    capabilities: [
      "PDF/lab report ingestion with OCR fallback",
      "Range-based flagging (normal/borderline/critical)",
      "Multi-visit trend charts for key biomarkers",
      "Plain-language AI summary with clinical disclaimers",
    ],
    technologies: ["Claude API", "RAG", "PDF parsing", "PostgreSQL"],
    frameworks: ["LangGraph"],
    image: "/portfolio/ai-agents/medical-report-insights-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "healthcare-software-development",
  },
  {
    name: "Claims Automation Agent",
    slug: "claims-automation-agent",
    category: "Healthcare",
    tier: 2,
    description: "Multi-agent pipeline that extracts data from insurance claim documents, validates against policy rules, and routes claims for approval or review.",
    problem: "Hospitals and insurers process claims manually, causing slow turnaround, duplicate data entry, and inconsistent validation.",
    capabilities: [
      "Document extraction (OCR + structured parsing)",
      "Policy rule validation engine",
      "Auto-routing: approve / flag / escalate",
      "Audit trail per claim decision",
    ],
    technologies: ["Claude API", "OCR", "PostgreSQL"],
    frameworks: ["CrewAI"],
    image: "/portfolio/ai-agents/claims-automation-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "healthcare-software-development",
  },
  {
    name: "Financial Reasoning Agent",
    slug: "financial-reasoning-agent",
    category: "Finance",
    tier: 1,
    description: "Pulls live market data and generates structured buy/hold/sell reasoning with supporting metrics, news sentiment, and risk notes for a given ticker.",
    problem: "Retail investors and advisors need fast, structured analysis instead of scanning dozens of tabs across price charts, filings, and news.",
    capabilities: [
      "Real-time price and fundamentals lookup",
      "AI-generated investment reasoning with citations",
      "News sentiment scoring",
      "Watchlist and alerting",
    ],
    technologies: ["Claude API", "Yahoo Finance API", "React"],
    frameworks: ["Direct tool-calling"],
    image: "/portfolio/ai-agents/financial-reasoning-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "fintech-app-development",
  },
  {
    name: "E-commerce Personal Shopper",
    slug: "ecommerce-personal-shopper-agent",
    category: "E-commerce",
    tier: 2,
    description: "Conversational shopping assistant that understands natural-language requests, searches the product catalog, and returns ranked recommendations with match scores.",
    problem: "Shoppers abandon search when filters are clunky and results don't match intent, hurting conversion on large catalogs.",
    capabilities: [
      "Natural-language product search",
      "Match-score ranking against stated preferences",
      "Auto-applied filter chips",
      "Side-by-side comparison view",
    ],
    technologies: ["Claude API", "Vector search", "Node.js"],
    frameworks: ["LangGraph"],
    image: "/portfolio/ai-agents/ecommerce-personal-shopper-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "ecommerce-development",
  },
  {
    name: "Email & Meeting Assistant",
    slug: "email-meeting-assistant-agent",
    category: "Enterprise Ops",
    tier: 2,
    description: "Drafts context-aware email replies and auto-schedules meetings with generated agendas, saving execs hours of inbox and calendar management weekly.",
    problem: "Executives lose hours daily triaging email and prepping for meetings with no structured agenda.",
    capabilities: [
      "AI-drafted reply suggestions with tone control",
      "Auto-scheduling against calendar availability",
      "Agenda generation from thread context",
      "Weekly time-saved reporting",
    ],
    technologies: ["Claude API", "Gmail API", "Calendar API"],
    frameworks: ["CrewAI Flows"],
    image: "/portfolio/ai-agents/email-meeting-assistant-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
  {
    name: "Legal Document Review Agent",
    slug: "legal-document-review-agent",
    category: "Legal",
    tier: 3,
    description: "Scans contracts and legal PDFs to extract key clauses, flag risk levels, and cite the exact source passage for every finding.",
    problem: "Manual contract review is slow and inconsistent; risky clauses get missed under deadline pressure.",
    capabilities: [
      "Clause extraction (termination, liability, indemnity, etc.)",
      "Risk-level scoring per clause",
      "Citation-grounded findings",
      "Exportable review summary",
    ],
    technologies: ["Claude API", "Vector embeddings", "PostgreSQL"],
    frameworks: ["LlamaIndex"],
    image: "/portfolio/ai-agents/legal-document-review-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
  {
    name: "Fleet & Logistics Optimization Agent",
    slug: "fleet-logistics-optimization-agent",
    category: "Logistics & Fleet",
    tier: 1,
    description: "Ingests live vehicle telemetry and generates optimized delivery routes, flagging reroutes that save time and fuel across an active fleet.",
    problem: "Dispatchers reroute manually with stale ETAs, missing fuel and time savings available from real-time optimization.",
    capabilities: [
      "Live telemetry ingestion",
      "Route optimization with reroute suggestions",
      "Fuel and on-time KPI tracking",
      "Fleet-wide status map",
    ],
    technologies: ["Claude API", "Mapping APIs", "PostgreSQL"],
    frameworks: ["LangGraph"],
    image: "/portfolio/ai-agents/fleet-logistics-optimization-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "fleet-management-software",
  },
  {
    name: "Property Pricing Agent",
    slug: "property-pricing-agent",
    category: "Real Estate",
    tier: 1,
    description: "Analyzes comparable listings and neighborhood trends to generate a defensible AI price estimate with a confidence range for any property.",
    problem: "Agents and buyers lack fast, data-backed pricing guidance without commissioning a full manual comps analysis.",
    capabilities: [
      "Comparable property matching with similarity scores",
      "Confidence-banded price estimate",
      "Neighborhood trend charting",
      "Market-factor breakdown (schools, days-on-market, etc.)",
    ],
    technologies: ["Claude API", "RAG", "PostgreSQL"],
    frameworks: ["Direct tool-calling"],
    image: "/portfolio/ai-agents/property-pricing-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
    industrySlug: "real-estate-proptech-solutions",
  },
  {
    name: "24/7 Customer Support Agent",
    slug: "24-7-customer-support-agent",
    category: "Enterprise Ops",
    tier: 2,
    description: "RAG-grounded chatbot that answers customer queries around the clock with cited sources and a clean handoff to a human agent when confidence is low.",
    problem: "Support teams can't staff round-the-clock coverage, and generic bots give unsourced, low-trust answers.",
    capabilities: [
      "RAG answers grounded in knowledge base with citations",
      "Confidence-based human handoff",
      "Sentiment tracking per conversation",
      "Deflection-rate and CSAT reporting",
    ],
    technologies: ["Claude API", "RAG", "WebSockets"],
    frameworks: ["LangGraph"],
    image: "/portfolio/ai-agents/24-7-customer-support-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
  {
    name: "Recruitment Screening Agent",
    slug: "recruitment-screening-agent",
    category: "HR",
    tier: 3,
    description: "Parses resumes against job requirements, scores candidate fit, and builds a shortlist pipeline with a skills-match breakdown.",
    problem: "Recruiters manually screen large applicant pools, slowing time-to-shortlist and introducing inconsistent criteria.",
    capabilities: [
      "Resume parsing and structured extraction",
      "Requirement-match scoring",
      "Shortlist pipeline (Applied → Screened → Interview)",
      "Bias-check flag on scoring criteria",
    ],
    technologies: ["Claude API", "PostgreSQL"],
    frameworks: ["CrewAI"],
    image: "/portfolio/ai-agents/recruitment-screening-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
  {
    name: "Lead Scoring & Outreach Agent",
    slug: "lead-scoring-outreach-agent",
    category: "Sales & Marketing",
    tier: 3,
    description: "Scores inbound leads on engagement, firmographics, and intent signals, then recommends the next outreach sequence to run.",
    problem: "Sales teams chase every lead equally, wasting time on low-intent contacts while high-intent leads go cold.",
    capabilities: [
      "0-100 lead scoring with explainable factors",
      "Hot/warm/cold segmentation",
      "Outreach sequence recommendation",
      "Conversion funnel reporting",
    ],
    technologies: ["Claude API", "CRM integration", "PostgreSQL"],
    frameworks: ["CrewAI"],
    image: "/portfolio/ai-agents/lead-scoring-outreach-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
  {
    name: "Travel Itinerary Agent",
    slug: "travel-itinerary-agent",
    category: "Travel",
    tier: 3,
    description: "Turns a natural-language trip request into a full day-by-day itinerary with activities, map pins, and a budget breakdown.",
    problem: "Trip planning across dozens of tabs is slow, and generic itineraries ignore stated budget and preferences.",
    capabilities: [
      "Natural-language trip intake",
      "Day-by-day itinerary generation",
      "Map-pinned activity locations",
      "Budget breakdown by category",
    ],
    technologies: ["Claude API", "MCP tools", "Mapping APIs"],
    frameworks: ["MCP"],
    image: "/portfolio/ai-agents/travel-itinerary-agent.webp",
    relatedServiceSlug: "ai-ml-solutions",
  },
];

export interface AIAgentsFAQ {
  question: string;
  answer: string;
}

export const aiAgentsFaqs: AIAgentsFAQ[] = [
  {
    question: "What AI agents does Ortem Technologies build?",
    answer: "Ortem Technologies builds and adapts AI agents across healthcare (medical report insights, claims automation), finance (financial reasoning and market analysis), e-commerce (personal shopping assistants), logistics and fleet (route optimization), real estate (property pricing), legal (document review), HR (recruitment screening), sales (lead scoring), and enterprise operations (email, meeting, and customer support agents).",
  },
  {
    question: "Are Ortem's AI agent examples live client projects?",
    answer: "No — the AI agents shown in our portfolio are in-house products and accelerators we build to demonstrate capability, not client deliverables. We use them as production-ready starting points that we customize for each client's specific data, workflows, and compliance requirements.",
  },
  {
    question: "What frameworks and models does Ortem use to build AI agents?",
    answer: "Ortem builds AI agents using Claude API and other LLMs, with agent orchestration frameworks including LangGraph, CrewAI, and LlamaIndex, combined with RAG pipelines, vector search, and tool-calling for production-grade multi-step automation.",
  },
  {
    question: "Can Ortem build a custom AI agent for my industry?",
    answer: "Yes. Ortem's AI & ML Solutions team scopes and builds custom AI agents for any industry, starting from the closest matching accelerator in our portfolio and adapting it to your data sources, compliance needs, and existing systems.",
  },
];
